"use client";

import useLocalizedRouter from "@/hooks/use-localized-router";
import { baseUrl } from "@/utils/api-definition";
import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import { Box, Button, ListItem, ListItemButton, ListItemText } from "@mui/material";
import ProductList from "@/components/core/product-list";
import AlertComponent from "@/components/core/alert";
import { useAlertStore } from "@/components/context/alert-store";
import { SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import Image from "next/image";
import Banner from "@/components/core/banner";
import { useSeoURLStore } from "@/components/context/seo-lang-store";
import CustomBreadcrumbs from "./custom-breadcrumbs";
import Slider from "@/components/core/slider";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import Link from "@/components/core/localized-link";
import { stickCustomHTML, filterLayouts } from "@/components/core/parse-custom-html";
import HtmlWithImage from "@/components/core/html-with-next-image";
import { ISimpleProductItem } from "@/types/product";
import { ICategoryData, IParentCategory } from "@/types/category";
import { ICollectionMeta } from "@/types/general";
import { useXNoticeStore } from "@/components/context/xnotice-store";
import XNoticeContent from "@/main/_components/xnotice-content/xnotice-content";

export default function Category({
  category,
  products,
  lang,
  meta,
}: Readonly<{
  category: ICategoryData;
  products: ISimpleProductItem[];
  meta: ICollectionMeta;
  lang: string;
}>) {
  const router = useLocalizedRouter();
  const data = category;
  const parentCategories = category.parent_categories;
  let innerHTML = data?.description;
  const { setCategoryIDs } = useXNoticeStore();

  if (baseUrl && innerHTML && !innerHTML.includes(' src="' + baseUrl)) {
    innerHTML = innerHTML.replace(' src="', ' src="' + baseUrl);
  }

  const subCategories = data?.sub_categories;
  const { setSeoLinks } = useSeoURLStore();
  const { message, setMessage } = useAlertStore();

  useEffect(() => {
    setSeoLinks(category.seo_urls);
    setCategoryIDs([...[String(category.id)]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const generateBreadcrumbs = (parents: IParentCategory[]) => {
    const breadcrumbs: { title: string; link?: string | null }[] = [];
    if (parents && parents.length) {
      const parent = parents[0];

      if (parent?.parent_categories instanceof Array && parent?.parent_categories.length > 0) {
        parent?.parent_categories?.map((parentCat: IParentCategory) => {
          breadcrumbs.push({
            title: parentCat.name,
            link: parentCat.seo_url ? `/category/${parentCat.seo_url}` : null,
          });
        });
      }
      breadcrumbs.push({ title: parent.name });
    }

    return breadcrumbs;
  };

  const [errors, setErrors] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(true);

  useEffect(() => {
    if (errors) {
      setShowAlert(false);
    }
  }, [errors]);

  const topSection = filterLayouts(
    category.layout?.content_top ? category.layout?.content_top : [],
  );
  const bottomSection = filterLayouts(
    category.layout?.content_bottom ? category.layout?.content_bottom : [],
  );

  return (
    <div className="mb-10 flex flex-col text-sm xs:pt-[75px] md:pt-0">
      <CustomBreadcrumbs crumbs={generateBreadcrumbs(parentCategories)} />
      <XNoticeContent settings={{ selector: "#product-category" }} />
      {showAlert && <AlertComponent message={message} setMessage={setMessage} />}
      <Typography sx={{ fontWeight: 600, fontSize: "28px" }}>{category?.name}</Typography>
      <XNoticeContent settings={{ selector: ".heading" }} />
      <Box
        sx={{
          mt: data.cat_image ? "24px" : 0,
          mb: data.cat_image ? { xs: "24px", sm: "30px" } : 0,
        }}
      >
        {data.cat_image && (
          <div className="catdesc">
            <HtmlWithImage html={innerHTML} priority lang={lang} />
          </div>
        )}
        {data.description && !data.cat_image && (
          <div className="catdesc">
            <HtmlWithImage html={innerHTML} priority lang={lang} />
          </div>
        )}
      </Box>

      {topSection.length > 0 && (
        <Box className="pb-4 text-sm [&_ul]:list-disc [&_ul]:pl-5">
          {topSection.map((module) => stickCustomHTML(module, lang))}
        </Box>
      )}
      {products.length === 0 && (
        <Box className="mt-3 flex flex-col gap-5">
          <Typography>
            <Trans>There are no products to list in the category.</Trans>
          </Typography>
          <Link prefetch={false} href={`/${lang ? lang : "de"}`}>
            <Button variant="contained" className="h-[35px] px-5 font-normal">
              <Trans>Continue</Trans>
            </Button>
          </Link>
        </Box>
      )}
      {subCategories?.length > 0 && (
        <Slider className="category-slider cateogory-page-slider mt-[15px]" spaceBetween={0}>
          {subCategories?.map((subCategory: ICategoryData, i: number) => (
            <SwiperSlide key={i} style={{ width: "fit-content" }}>
              <ListItem disablePadding sx={{ width: "fit-content" }}>
                <ListItemButton
                  disableRipple
                  component="a"
                  onClick={() => router.push(`/category/${subCategory.seo_url}`)}
                  sx={{
                    color: "#000",
                    background: "rgba(238, 238, 239, 0.5)",
                    padding: "8px 15px",
                    border: "solid 1px #dfdfe0",
                    borderRadius: "8px",
                    maxWidth: "310px",
                    overflow: "hidden",
                    height: "50px",
                    transition: ".3s ease",
                    marginRight: "15px",
                    "&:hover": {
                      color: "primary.main",
                    },
                  }}
                >
                  <ListItemText
                    primary={subCategory.name}
                    sx={{
                      ".MuiTypography-root": {
                        fontSize: "16px",
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </SwiperSlide>
          ))}
        </Slider>
      )}
      <ProductList lang={lang} products={products} meta={meta} setErrors={setErrors} />
      <XNoticeContent settings={{ selector: ".wbpageshow" }} />
      {bottomSection.length > 0 && (
        <Box className="pb-4 text-sm [&_ul]:list-disc [&_ul]:pl-5">
          {bottomSection.map((module) => stickCustomHTML(module, lang))}
        </Box>
      )}
      {bottomSection.length === 0 && (
        <Banner
          subTitle={t`Offerz Voucher Designer`}
          title={t`Give personalized vouchers`}
          paragraph={t`With our voucher designer, you can easily give personalized vouchers with text, video, or photo messages!`}
          buttonText={t`Go to designer`}
          buttonLink="/create-gift"
          rightPadding="80px 30px 70px 30px"
          marginBottom="50px"
        >
          <div className="relative flex basis-1/2 justify-center">
            <Image
              loading="lazy"
              className="hidden md:block"
              src="https://offerz.ch/image/catalog/woman-phone.png"
              alt="woman-phone-image"
              width={371}
              height={520}
            />
            <Image
              loading="lazy"
              className="-ml-[65px] md:hidden"
              src="https://offerz.ch/image/catalog/woman-phone_mobile.png"
              alt="woman-phone-mobile-image"
              width={203}
              height={317}
            />
            <Image
              loading="lazy"
              className="absolute left-[56%] top-[130px] hidden max-w-[250px] md:block"
              src="https://offerz.ch/image/catalog/mockup-designer.png"
              alt="phone-image"
              width={274}
              height={520}
            />
            <Image
              loading="lazy"
              className="absolute left-[50%] top-[80px] md:hidden"
              src="https://offerz.ch/image/catalog/mockup-designer.png"
              alt="phone-image"
              width={159}
              height={280}
            />
          </div>
        </Banner>
      )}
    </div>
  );
}
